import {
    isMobile,
    isTablet,
    deviceType,
    osName,
    browserName,
} from "react-device-detect";

const supportsMatchMedia = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function";

// True only when the browser explicitly confirms the PRIMARY input device
// is a precise pointer (a mouse/trackpad). Real phones never report this,
// even ones with a stylus like the Galaxy Note's S-Pen (that only affects
// `any-pointer`, not the primary `pointer`). Fails open (false) on browsers
// that don't support the pointer media feature at all — some in-app
// browsers (Gmail/WhatsApp/Outlook) and older WebViews don't — so it never
// blocks a real phone whose browser lacks support for it.
const primaryPointerIsMouse = () =>
    supportsMatchMedia() && window.matchMedia("(pointer: fine)").matches;

/**
 * User-Agent sniffing (react-device-detect) can be spoofed from desktop
 * devtools by resizing the viewport ("responsive mode") without emulating
 * a specific device, which keeps the primary pointer a mouse. This adds
 * that as a secondary signal on top of the User-Agent check. Deliberately
 * avoids `any-pointer`/`maxTouchPoints`-based checks: those vary too much
 * across real phone browsers (stylus-enabled phones, privacy-hardened
 * browsers that zero out maxTouchPoints, in-app WebViews) and caused
 * legitimate phones to be blocked.
 */
export const isVerifiedMobileDevice = () => {
    const userAgentSaysMobile = isMobile || isTablet;
    if (!userAgentSaysMobile) return false;

    return !primaryPointerIsMouse();
};

/**
 * Bundles every device-type signal this app relies on into one object, so
 * Datadog logs can tell whether a scan attempt actually came from a phone
 * or from a desktop/laptop (e.g. via devtools responsive mode). Spread this
 * into a log's metadata: `{...getDeviceContext(), invitationId}`.
 */
export const getDeviceContext = () => ({
    // 'mobile' | 'tablet' | 'browser' (desktop) | 'smarttv' | ... (react-device-detect)
    deviceType,
    osName,
    browserName,
    userAgentSaysMobileOrTablet: isMobile || isTablet,
    isVerifiedMobileDevice: isVerifiedMobileDevice(),
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
});
